<template>
  <div class="rag-storage">
    <div class="storage-header">
      <div class="storage-indicator">
        <span class="status-dot" :class="statusColor"></span>
        <span class="storage-text">
          RAG Storage:
          <strong>{{ formatBytes(storageBreakdown.total_size) }}</strong> of
          {{ formatBytes(storageBreakdown.max_size) }}
        </span>
      </div>

      <div class="storage-details">
        <div class="storage-breakdown">
          <h4>Storage Breakdown ({{ usagePercentage }}% used)</h4>
          <div class="usage-info">
            <span class="usage-label-wrapper">
              <span class="usage-label">Total RAG usage</span>
              <div class="usage-tooltip">
                The total storage space used by all RAG indexes.
              </div>
            </span>
            <span> - </span>
            <strong>{{ formatBytes(storageBreakdown.total_size) }}</strong>
            <span class="usage-total">
              of {{ formatBytes(storageBreakdown.max_size) }}
            </span>
          </div>
          <div class="progress-bar">
            <div
              class="progress-fill"
              :class="statusColor"
              :style="{ width: usagePercentage + '%' }"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
export default {
  name: 'RAGStorage',
  data() {
    return {};
  },
  computed: {
    ...mapGetters({
      getMeta: 'tools/getMetaKnowledge',
    }),
    storageBreakdown() {
      return (
        this.getMeta.reports || {
          total_size: 0,
          max_size: 2.0,
          unit: 'MB',
        }
      );
    },
    usagePercentage() {
      const used = this.storageBreakdown.total_size || 0;
      const total = this.storageBreakdown.max_size || 1;
      return Math.min(100, Math.round((used / total) * 100));
    },
    statusColor() {
      const percentage = this.usagePercentage;
      if (percentage < 50) {
        return 'status-green';
      }

      if (percentage < 80) {
        return 'status-yellow';
      }

      return 'status-red';
    },
  },
  methods: {
    formatBytes(value) {
      if (value === 0 || value === undefined || value === null) {
        return '0 B';
      }
      const unit = this.storageBreakdown.unit || 'MB';
      return `${parseFloat(value).toFixed(2)} ${unit}`;
    },
  },
};
</script>

<style scoped>
.rag-storage {
  position: relative;
}

.storage-header {
  position: relative;
  background: #fff;
  border: 1px solid var(--s-75);
  border-radius: 24px;
  padding: 12px 24px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.storage-header:hover {
  background: var(--s-50);
  border-color: var(--s-100);
}

.storage-header:hover .storage-details {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.storage-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.status-dot.status-green {
  background: #4ade80;
  box-shadow: 0 0 8px rgba(74, 222, 128, 0.5);
}

.status-dot.status-yellow {
  background: #fbbf24;
  box-shadow: 0 0 8px rgba(251, 191, 36, 0.5);
}

.status-dot.status-red {
  background: #ef4444;
  box-shadow: 0 0 8px rgba(239, 68, 68, 0.5);
}

.storage-text {
  color: #333;
  font-size: 14px;
  white-space: nowrap;
}

.storage-text strong {
  font-weight: 600;
}

.storage-details {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 400px;
  background: #fff;
  border: 1px solid var(--s-200);
  border-radius: 16px;
  padding: 24px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.3s ease;
  z-index: 1000;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.storage-breakdown h4 {
  color: var(--s-900);
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 16px 0;
}

.usage-info {
  color: var(--s-700);
  font-size: 14px;
  margin-bottom: 12px;
}

.usage-label-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.usage-label {
  color: var(--s-900);
  border-bottom: 1px dotted var(--s-400);
  cursor: help;
  position: relative;
  z-index: 1;
}

.usage-label:hover + .usage-tooltip {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}

.usage-tooltip {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%) translateY(-4px);
  background: #fff;
  color: var(--s-900);
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  z-index: 1001;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.usage-tooltip:hover {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}

.usage-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 4px solid transparent;
  border-top-color: #1f1f1f;
}

.usage-total {
  color: #888;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: var(--w-100);
  border-radius: 4px;
  overflow: hidden;
  margin-top: 8px;
}

.progress-fill {
  height: 100%;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.progress-fill.status-green {
  background: linear-gradient(90deg, #4ade80 0%, #22c55e 100%);
  border-radius: 4px;
}

.progress-fill.status-yellow {
  background: linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%);
  border-radius: 4px;
}

.progress-fill.status-red {
  background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
  border-radius: 4px;
}
</style>
